import React from "react";
import { X, Truck, CheckCircle2, AlertTriangle, Loader2, Zap, Power } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "../lib/api";

/**
 * Modal/wizard til at åbne sitet for kunder.
 * - Viser nuværende availability + bypass-state
 * - Lister alle chauffør-kandidater med status
 * - One-click "Aktivér" pr. række
 * - Toggle midlertidig bypass (FORCE_ACCEPT_ORDERS runtime-flag)
 */
export default function OpenForBusinessWizard({ open, onClose, onChanged }) {
  const [data, setData] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [busyId, setBusyId] = React.useState(null);
  const [bypassBusy, setBypassBusy] = React.useState(false);
  const [softBusy, setSoftBusy] = React.useState(false);
  const [softResult, setSoftResult] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const { data: r } = await adminApi().get("/admin/availability/wizard");
      setData(r);
    } catch (e) {
      toast.error("Kunne ikke hente chauffør-status");
    } finally { setLoading(false); }
  }, []);

  React.useEffect(() => { if (open) load(); }, [open, load]);

  if (!open) return null;

  const activate = async (id, name) => {
    setBusyId(id);
    try {
      const { data: r } = await adminApi().post(`/admin/availability/activate-driver/${id}`);
      toast.success(`${name || "Chauffør"} er aktiveret — sitet er åbent for kunder`);
      setData((d) => d ? { ...d, current: r.availability } : d);
      await load();
      onChanged?.(r.availability);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke aktivere chauffør");
    } finally { setBusyId(null); }
  };

  const toggleBypass = async (enabled) => {
    setBypassBusy(true);
    try {
      const { data: r } = await adminApi().post(`/admin/availability/bypass`, { enabled });
      toast.success(enabled ? "Bypass tændt — sitet accepterer bookinger uden chauffører" : "Bypass slukket");
      setData((d) => d ? { ...d, current: r.availability } : d);
      onChanged?.(r.availability);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke ændre bypass");
    } finally { setBypassBusy(false); }
  };

  const runSoftLaunch = async () => {
    setSoftBusy(true);
    try {
      const { data: r } = await adminApi().post(`/admin/availability/soft-launch`);
      toast.success(`Testbooking ${r.booking_number} oprettet`);
      setSoftResult(r);
      await load();
      onChanged?.(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Kunne ikke starte soft-launch");
    } finally { setSoftBusy(false); }
  };

  const current = data?.current || {};
  const isOpen = current.accepting_orders;
  const isBypass = current.bypass_active || current.reason === "force_accept_runtime";

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" data-testid="open-for-business-wizard">
      <div className="bg-white border border-brand max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-brand-dark text-white p-5 flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-widest text-amber-300 font-bold">Åbn for kunder</div>
            <h2 className="font-display font-bold text-2xl mt-1">
              {isOpen ? "Sitet er åbent 🚀" : "Sitet er lukket — 0 aktive chauffører"}
            </h2>
            <p className="text-xs text-white/70 mt-1">
              {isOpen
                ? isBypass
                  ? "Bookinger accepteres via bypass — onboard chauffører for at slå bypass fra."
                  : `${current.active_drivers} aktive · ${current.online_drivers >= 0 ? current.online_drivers : "?"} online sidste 5 min.`
                : "Kunder kan ikke booke før mindst 1 chauffør er aktiv."}
            </p>
          </div>
          <button onClick={onClose} className="text-white/70 hover:text-white" data-testid="wizard-close">
            <X size={20} />
          </button>
        </div>

        {/* Status banner */}
        <div className="p-5 space-y-4">
          {loading ? (
            <div className="text-center py-8 text-brand-text2"><Loader2 size={20} className="animate-spin mx-auto" /></div>
          ) : (
            <>
              {/* Bypass toggle */}
              <div className={`border p-4 ${isBypass ? "bg-amber-50 border-amber-300" : "bg-brand-bg/30 border-brand"}`} data-testid="bypass-section">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base text-brand-dark flex items-center gap-2">
                      <Zap size={16} className={isBypass ? "text-amber-700" : "text-brand-text2"} />
                      Midlertidig bypass
                    </div>
                    <p className="text-xs text-brand-text2 mt-1 max-w-xl">
                      Tving sitet åbent uden aktive chauffører — brug kun til soft-launch eller tests.
                      Husk at slukke når du har onboardet rigtige chauffører.
                    </p>
                    {isBypass && current.bypass_set_by && (
                      <p className="text-xs text-amber-700 mt-2">
                        Slået til af <b>{current.bypass_set_by}</b> · {new Date(current.bypass_set_at).toLocaleString("da-DK")}
                      </p>
                    )}
                  </div>
                  <button
                    onClick={() => toggleBypass(!isBypass)}
                    disabled={bypassBusy}
                    className={`px-4 py-2 text-sm font-bold flex items-center gap-2 transition-colors disabled:opacity-60 flex-shrink-0 ${
                      isBypass
                        ? "bg-amber-700 text-white hover:bg-amber-800"
                        : "border border-brand-dark text-brand-dark hover:bg-brand-dark hover:text-white"
                    }`}
                    data-testid="bypass-toggle"
                  >
                    {bypassBusy ? <Loader2 size={14} className="animate-spin" /> : <Power size={14} />}
                    {isBypass ? "Sluk bypass" : "Tænd bypass"}
                  </button>
                </div>
              </div>

              {/* Driver list */}
              <div>
                <h3 className="font-display font-bold text-base text-brand-dark mb-2 flex items-center gap-2">
                  <Truck size={16} className="text-brand-red" />
                  Chauffør-kandidater ({data?.total_drivers || 0})
                </h3>
                {(data?.candidates || []).length === 0 ? (
                  <div className="bg-yellow-50 border border-yellow-300 p-4 text-sm text-yellow-900" data-testid="no-drivers">
                    <AlertTriangle size={14} className="inline mr-1" />
                    Ingen chauffører oprettet endnu. Gå til <b>Chauffør-konti</b>-tabben for at oprette den første.
                  </div>
                ) : (
                  <div className="bg-white border border-brand divide-y divide-brand">
                    {(data.candidates || []).map((d) => (
                      <div key={d.id} className="p-3 flex items-start justify-between gap-3 flex-wrap" data-testid={`driver-row-${d.id}`}>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display font-bold text-sm text-brand-dark">{d.name || "(uden navn)"}</span>
                            {d.status === "active" && (
                              <span className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-900 border border-emerald-300 font-bold uppercase" data-testid={`status-${d.id}-active`}>
                                <CheckCircle2 size={10} className="inline mr-1" /> Aktiv
                              </span>
                            )}
                            {d.status === "onboarding" && (
                              <span className="text-xs px-2 py-0.5 bg-amber-100 text-amber-900 border border-amber-300 font-bold uppercase">
                                Onboarding
                              </span>
                            )}
                            {d.status === "deactivated" && (
                              <span className="text-xs px-2 py-0.5 bg-red-100 text-red-900 border border-red-300 font-bold uppercase">
                                Deaktiveret
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-brand-text2 mt-0.5">
                            {d.email} {d.phone && <>· {d.phone}</>} · oprettet {d.created_at ? new Date(d.created_at).toLocaleDateString("da-DK") : "?"}
                          </div>
                        </div>
                        {d.status !== "active" && (
                          <button
                            onClick={() => activate(d.id, d.name)}
                            disabled={busyId === d.id}
                            className="bg-emerald-700 text-white hover:bg-emerald-800 px-3 py-2 text-xs font-bold flex items-center gap-1 transition-colors disabled:opacity-60 flex-shrink-0"
                            data-testid={`activate-${d.id}`}
                          >
                            {busyId === d.id ? <Loader2 size={11} className="animate-spin" /> : <Zap size={11} />}
                            Aktivér nu
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="bg-blue-50 border border-blue-300 p-3 text-xs text-blue-900">
                <b>Tip:</b> "Aktivér nu" sætter chaufføren som onboardet og aktiv. Bypass-tilstanden gemmes i databasen og virker øjeblikkeligt — ingen restart nødvendig.
              </div>

              {/* Soft-launch */}
              <div className="bg-gradient-to-br from-indigo-900 to-brand-dark text-white p-4 border-l-4 border-amber-400" data-testid="soft-launch-section">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-base flex items-center gap-2">
                      <Zap size={16} className="text-amber-300" />
                      Soft-launch (test hele kunderejsen)
                    </div>
                    <p className="text-xs text-white/70 mt-1 max-w-xl">
                      Tænder bypass <i>og</i> opretter en gratis testbooking på din egen email.
                      Brug til at validere booking → tracking → leverance før du åbner for rigtige kunder.
                    </p>
                  </div>
                  <button
                    onClick={runSoftLaunch}
                    disabled={softBusy}
                    className="bg-amber-400 text-brand-dark hover:bg-amber-300 px-4 py-2 text-sm font-bold flex items-center gap-2 transition-colors disabled:opacity-60 flex-shrink-0"
                    data-testid="soft-launch-trigger"
                  >
                    {softBusy ? <Loader2 size={14} className="animate-spin" /> : <Zap size={14} />}
                    Start soft-launch
                  </button>
                </div>
                {softResult && (
                  <div className="mt-3 bg-amber-100 border border-amber-300 text-amber-900 px-3 py-2 text-xs" data-testid="soft-launch-result">
                    <b>{softResult.booking_number}</b> oprettet — <a href={softResult.tracking_url} target="_blank" rel="noreferrer" className="underline font-bold">åbn tracking →</a>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
